/**
 * Speaker profiles -- group 03 (British passive designs and studio actives).
 *
 * Three passive designs and five powered monitors. The passive entries present
 * a real reactive load, so their impedance curves are derived from the stated
 * alignment and will interact with a low-damping-factor amplifier. The active
 * entries do not: the only electrical load they present upstream is a balanced
 * line input, so their impedance is authored explicitly as a flat 10 kOhm and
 * the amplifier-interaction path of the engine correctly does nothing. Their
 * `sensitivityDb` is likewise not a passive sensitivity -- see each
 * provenanceNote for the convention used.
 */

import type { SpeakerProfile } from '../../types/hardware';
import { impedanceCurve, makeSpeaker, usd } from '../kit';

export const SPEAKERS_03: SpeakerProfile[] = [
  makeSpeaker({
    mfr: 'Kudos Audio',
    model: 'Titan 606',
    price: usd(9500, 'APPROXIMATE', 2021, 'Approximate US retail per pair; Kudos quotes in GBP.'),
    year: 2016,
    country: 'United Kingdom',
    speakerType: 'standmount',
    enclosure: 'ported',
    drivers: ['dynamic'],
    driverCount: 2,
    wayCount: 2,
    sensitivityDb: 87,
    nominalOhms: 8,
    minOhms: 6.0,
    bass: [38, 30],
    hfExtension: 26000,
    portTuningHz: 36,
    crossovers: [{ freqHz: 2400, slopeDbOct: 6, alignment: 'first-order' }],
    responseTilt: [[20, 0], [60, 0.4], [150, -0.3], [400, -0.2], [1000, 0], [2500, 0.5], [5000, 0.2], [10000, 0.6], [20000, -0.8]],
    resonances: [[210, -32, 9], [520, -36, 12]],
    coverageAngleDeg: 120,
    recommendedWatts: [50, 200],
    dimensionsMm: [430, 200, 300],
    massKg: 14,
    thdPercent: 0.35,
    roomCoupling: 'medium',
    confidence: 'ESTIMATED',
    accent: '#7f9c8b',
    tags: ['british', 'first-order-crossover', 'active-ready'],
    notes:
      'The defining decision is the crossover order. A nominally first-order acoustic transition at 2.4 kHz leaves both drivers contributing over more than two octaves, which keeps phase rotation through the crossover to about 90 degrees instead of the 360 a fourth-order alignment imposes, but it also forces the tweeter to work an octave below crossover where its excursion and therefore its distortion rise fastest. That trade is why the model pairs a shallow crossover with a higher distortion figure than the studio designs here. The cabinet is a conventional damped reflex tuned just below the -3 dB point, and the same drivers are used in the manufacturer active configuration, so the passive network is the only thing being modelled.',
    provenanceNote:
      'Driver complement, standmount reflex format and the low-order crossover philosophy are from Kudos published material. Sensitivity, nominal and minimum impedance, bass extension, port tuning, crossover frequency, mass and dimensions are ESTIMATED from the product class; Kudos publishes an abbreviated specification. Anechoic response, directivity and cabinet resonances are MODELLED.',
  }),

  makeSpeaker({
    mfr: 'Russell K',
    model: 'Red 150',
    price: usd(5995, 'APPROXIMATE', 2020, 'Approximate US retail per pair.'),
    year: 2016,
    country: 'United Kingdom',
    speakerType: 'floorstander',
    enclosure: 'ported',
    drivers: ['dynamic'],
    driverCount: 3,
    wayCount: 2,
    sensitivityDb: 88,
    nominalOhms: 8,
    minOhms: 6.4,
    bass: [33, 27],
    hfExtension: 24000,
    portTuningHz: 32,
    crossovers: [[2700, 12]],
    responseTilt: [[20, 0], [50, 0.6], [100, 0.5], [250, -0.2], [800, 0.2], [2000, -0.6], [4000, -0.2], [8000, 0.4], [16000, -1.0]],
    resonances: [[330, -33, 5], [760, -37, 7]],
    coverageAngleDeg: 125,
    recommendedWatts: [30, 150],
    dimensionsMm: [900, 180, 260],
    massKg: 18,
    thdPercent: 0.4,
    roomCoupling: 'medium',
    confidence: 'ESTIMATED',
    accent: '#b4453f',
    tags: ['british', 'undamped-cabinet', 'shelf-braced', 'reflex'],
    notes:
      'The cabinet carries no damping wadding at all. Internal shelf braces instead divide the volume into coupled sub-chambers so that the internal standing waves land where the geometry can control them, rather than being absorbed. Modelled as two low-Q cabinet resonances placed higher in frequency and about 6 dB lower in level than a stuffed box of the same size would show, with a correspondingly greater midrange contribution through the port. The two mid-bass drivers run in parallel over the whole range below a 2.7 kHz second-order crossover, which is why the modelled sensitivity is a dB higher than the standmount here and the vertical pattern narrows earlier.',
    provenanceNote:
      'Cabinet philosophy with no internal damping material and internal shelf bracing, plus the two-way floorstanding format, are from Russell K published material. Sensitivity, impedance, bass extension, port tuning, crossover frequency, dimensions and mass are ESTIMATED. Cabinet resonance frequencies, levels and Q values are MODELLED consequences of the stated construction, not measurements.',
  }),

  makeSpeaker({
    mfr: 'Amphion',
    model: 'Argon 3S',
    price: usd(4500, 'APPROXIMATE', 2020, 'Approximate US retail per pair.'),
    year: 2016,
    country: 'Finland',
    speakerType: 'standmount',
    enclosure: 'passive-radiator',
    drivers: ['dynamic'],
    driverCount: 3,
    wayCount: 2,
    sensitivityDb: 86,
    nominalOhms: 8,
    minOhms: 6.2,
    bass: [35, 28],
    hfExtension: 25000,
    portTuningHz: 34,
    crossovers: [[1600, 12]],
    responseTilt: [[20, 0], [80, 0.2], [200, -0.4], [700, 0], [1600, -0.3], [3000, 0.3], [7000, -0.2], [12000, -0.4], [20000, -1.2]],
    resonances: [[190, -34, 8], [470, -38, 11]],
    coverageAngleDeg: 80,
    recommendedWatts: [25, 200],
    dimensionsMm: [510, 180, 265],
    massKg: 12,
    thdPercent: 0.3,
    roomCoupling: 'medium',
    confidence: 'ESTIMATED',
    accent: '#5d7f9e',
    tags: ['waveguide', 'controlled-directivity', 'passive-radiator', 'low-crossover'],
    notes:
      'Two unusual choices work together. The tweeter sits in a shallow waveguide, which raises its sensitivity at the bottom of its range and narrows its pattern to roughly 80 degrees, so the crossover can be dropped to 1600 Hz without asking the dome to move too far; the directivity index is then continuous through the transition instead of stepping, which is what dominates the reflected field in a normal room. Bass is loaded by a passive radiator rather than a port, so there is no organ-pipe resonance and no port compression at high level, at the cost of a fourth-order roll-off with a steeper group-delay rise just above tuning.',
    provenanceNote:
      'Waveguide-loaded tweeter, the low crossover point and passive radiator bass loading are from Amphion published material. Sensitivity, impedance, bass extension, radiator tuning, dimensions and mass are ESTIMATED from the product class. Coverage angle, anechoic response and cabinet resonances are MODELLED.',
  }),

  makeSpeaker({
    mfr: 'Genelec',
    model: '8351B',
    price: usd(
      5495,
      'APPROXIMATE',
      2021,
      'Approximate US retail each; Genelec studio products are priced per unit, not per pair.',
    ),
    year: 2018,
    country: 'Finland',
    speakerType: 'coaxial',
    enclosure: 'ported',
    drivers: ['coaxial-point-source', 'dynamic'],
    driverCount: 4,
    wayCount: 3,
    active: true,
    sensitivityDb: 96,
    nominalOhms: 10000,
    impedance: impedanceCurve(
      [[20, 10000, 0], [1000, 10000, 0], [10000, 9600, -8], [20000, 9000, -14]],
      10000,
      'ESTIMATED',
    ),
    bass: [38, 32],
    hfExtension: 43000,
    portTuningHz: 36,
    crossovers: [
      { freqHz: 500, slopeDbOct: 48, alignment: 'digital-lr' },
      { freqHz: 1300, slopeDbOct: 48, alignment: 'digital-lr' },
    ],
    responseTilt: [[20, 0], [60, 0.2], [150, -0.2], [500, 0.1], [1500, -0.2], [4000, 0.2], [10000, -0.3], [20000, -0.5]],
    resonances: [[240, -44, 16], [610, -48, 20]],
    coverageAngleDeg: 110,
    dimensionsMm: [452, 287, 278],
    massKg: 19,
    thdPercent: 0.25,
    roomCoupling: 'medium',
    confidence: 'MANUFACTURER_SPEC',
    accent: '#9fb0bd',
    tags: ['active', 'dsp-crossover', 'coaxial', 'studio-monitor'],
    notes:
      'A coincident midrange and tweeter with two woofers hidden behind the baffle so that the whole three-way behaves as one point source above about 500 Hz, with steep DSP crossovers and phase correction rather than passive networks. The consequences the model carries are a very flat authored voicing, low cabinet resonance levels from the rounded minimum-diffraction enclosure, and a wide but even 110 degree pattern that does not step at the crossovers. Because it is active, the amplifier is inside the box: the profile presents a flat 10 kOhm balanced input upstream, so no amplifier in the chain can modulate its response.',
    provenanceNote:
      'Coaxial three-way format, DSP crossover with phase correction, bass extension and physical dimensions are from Genelec published specifications. Crossover frequencies, cabinet resonances and the anechoic response shape are MODELLED. Sensitivity is not a meaningful parameter for an active design: the value recorded here is the modelled 1 m on-axis SPL for a nominal 0 dBu balanced input at the factory gain setting and is ESTIMATED. The impedance curve is the line input, not a driver load.',
  }),

  makeSpeaker({
    mfr: 'Neumann',
    model: 'KH 420',
    price: usd(5000, 'APPROXIMATE', 2020, 'Approximate US retail each.'),
    year: 2013,
    country: 'Germany',
    speakerType: 'active',
    enclosure: 'ported',
    drivers: ['dynamic'],
    driverCount: 3,
    wayCount: 3,
    active: true,
    sensitivityDb: 98,
    nominalOhms: 10000,
    impedance: impedanceCurve(
      [[20, 10000, 0], [1000, 10000, 0], [10000, 9500, -9], [20000, 8800, -15]],
      10000,
      'ESTIMATED',
    ),
    bass: [30, 26],
    hfExtension: 22000,
    portTuningHz: 28,
    crossovers: [[650, 24], [2000, 24]],
    responseTilt: [[20, 0], [60, 0.3], [120, -0.3], [400, 0.2], [1000, 0], [3000, -0.4], [7000, 0.3], [14000, -0.2], [20000, -1.0]],
    resonances: [[175, -40, 12], [430, -44, 15]],
    coverageAngleDeg: 95,
    dimensionsMm: [634, 380, 388],
    massKg: 32,
    thdPercent: 0.3,
    roomCoupling: 'medium',
    confidence: 'ESTIMATED',
    accent: '#8a8f96',
    tags: ['active', 'analog-crossover', 'mmd-waveguide', 'large-format'],
    notes:
      'An analog active design, which distinguishes it from the DSP monitors in this group: the crossovers are fourth-order Linkwitz-Riley networks in the analog domain at 650 Hz and 2 kHz, so there is no processing latency and no linear-phase correction, and the through-crossover phase behaves exactly as a minimum-phase system. Tonal shaping is done with switched acoustic controls rather than filters computed per unit. The elliptical waveguide narrows the horizontal pattern to roughly 95 degrees and holds it constant through the upper crossover, and the large ported woofer gives the deepest reflex extension of the powered entries here.',
    provenanceNote:
      'Three-way active format, waveguide-loaded tweeter, analog crossover topology, bass extension and dimensions are from Neumann published specifications. Crossover frequencies, cabinet resonances and anechoic response shape are MODELLED. Sensitivity follows the active convention used across this group: modelled 1 m SPL for a nominal 0 dBu balanced input at factory gain, ESTIMATED. The impedance curve is the line input.',
  }),

  makeSpeaker({
    mfr: 'ADAM Audio',
    model: 'S3V',
    price: usd(3299, 'APPROXIMATE', 2020, 'Approximate US retail each.'),
    year: 2017,
    country: 'Germany',
    speakerType: 'active',
    enclosure: 'ported',
    drivers: ['amt', 'dynamic'],
    driverCount: 3,
    wayCount: 3,
    active: true,
    sensitivityDb: 97,
    nominalOhms: 10000,
    impedance: impedanceCurve(
      [[20, 10000, 0], [1000, 10000, 0], [10000, 9700, -7], [20000, 9200, -12]],
      10000,
      'ESTIMATED',
    ),
    bass: [35, 30],
    hfExtension: 50000,
    portTuningHz: 33,
    crossovers: [
      { freqHz: 250, slopeDbOct: 48, alignment: 'digital-lr' },
      { freqHz: 3000, slopeDbOct: 48, alignment: 'digital-lr' },
    ],
    responseTilt: [[20, 0], [70, 0.3], [200, -0.4], [800, 0.1], [2500, -0.3], [6000, 0.4], [12000, 0.5], [20000, 0.2], [40000, -1.5]],
    resonances: [[205, -42, 14], [520, -46, 18]],
    coverageAngleDeg: 100,
    dimensionsMm: [510, 290, 395],
    massKg: 26,
    thdPercent: 0.25,
    roomCoupling: 'medium',
    confidence: 'ESTIMATED',
    accent: '#d06a3c',
    tags: ['active', 'amt-tweeter', 'dsp-crossover', 'studio-monitor'],
    notes:
      'The tweeter is a folded-diaphragm air-motion transformer, not a dome. Its pleated membrane squeezes air out of the folds at several times the diaphragm velocity, so it reaches 50 kHz with very little excursion and its distortion does not climb at the top of its range the way a 25 mm dome does near its own resonance. The authored voicing therefore carries a slight rise above 6 kHz that is real output rather than a break-up peak. Below it a midrange dome takes over at 250 Hz, an unusually low mid crossover that keeps the woofer out of the vocal range entirely, and the DSP handles the steep 48 dB per octave transitions.',
    provenanceNote:
      'Three-way active format, X-ART air-motion-transformer tweeter, 50 kHz upper limit and DSP-managed crossovers are from ADAM Audio published specifications. Crossover frequencies, cabinet resonances and anechoic response shape are MODELLED. Sensitivity follows the active convention used across this group: modelled 1 m SPL for a nominal 0 dBu balanced input at factory gain, ESTIMATED. The impedance curve is the line input.',
  }),

  makeSpeaker({
    mfr: 'Barefoot Sound',
    model: 'MicroMain26',
    price: usd(6995, 'APPROXIMATE', 2020, 'Approximate US retail each.'),
    year: 2014,
    country: 'United States',
    speakerType: 'active',
    enclosure: 'sealed',
    drivers: ['dynamic'],
    driverCount: 4,
    wayCount: 3,
    active: true,
    sensitivityDb: 95,
    nominalOhms: 10000,
    impedance: impedanceCurve(
      [[20, 10000, 0], [1000, 10000, 0], [10000, 9600, -8], [20000, 9000, -13]],
      10000,
      'ESTIMATED',
    ),
    bass: [30, 24],
    hfExtension: 24000,
    crossovers: [[180, 24], [3000, 24]],
    responseTilt: [[20, 0], [50, 0.4], [120, 0.2], [350, -0.3], [1000, 0], [2500, 0.3], [5000, -0.4], [10000, 0.2], [18000, -0.8]],
    resonances: [[260, -46, 18], [640, -50, 22]],
    coverageAngleDeg: 105,
    dimensionsMm: [432, 330, 330],
    massKg: 34,
    thdPercent: 0.28,
    roomCoupling: 'low',
    confidence: 'ESTIMATED',
    accent: '#6b6f76',
    tags: ['active', 'sealed', 'force-cancelling', 'studio-monitor'],
    notes:
      'Two woofers are mounted in opposition and driven in phase acoustically but in mechanical anti-phase, so their reaction forces cancel inside the cabinet and the enclosure itself barely moves. That is modelled as cabinet resonances roughly 10 dB below the passive designs in this group at a similar size. The alignment is sealed, giving a second-order roll-off: less extension on paper than the ported monitors here, but a shallower slope below the corner, lower group delay, and better behaviour when room gain adds to the bottom octave, which is why room coupling is set low. The switchable voicing emulations are alternative target curves and are deliberately not baked into this profile.',
    provenanceNote:
      'Sealed force-cancelling dual-woofer configuration, three-way active format and physical size are from Barefoot Sound published material. Sensitivity, bass extension, crossover frequencies, cabinet resonances and anechoic response shape are ESTIMATED or MODELLED; Barefoot publishes a short specification. Sensitivity follows the active convention used across this group. The impedance curve is the line input.',
  }),

  makeSpeaker({
    mfr: 'Kii Audio',
    model: 'Kii Three',
    price: usd(14995, 'APPROXIMATE', 2021, 'Approximate US retail per pair, without the BXT bass extension.'),
    year: 2016,
    country: 'Germany',
    speakerType: 'active',
    enclosure: 'sealed',
    drivers: ['dynamic'],
    driverCount: 6,
    wayCount: 3,
    active: true,
    sensitivityDb: 100,
    nominalOhms: 10000,
    impedance: impedanceCurve(
      [[20, 10000, 0], [1000, 10000, 0], [10000, 9800, -6], [20000, 9400, -11]],
      10000,
      'ESTIMATED',
    ),
    bass: [20, 16],
    hfExtension: 25000,
    crossovers: [
      { freqHz: 400, slopeDbOct: 48, alignment: 'digital-lr' },
      { freqHz: 2500, slopeDbOct: 48, alignment: 'digital-lr' },
    ],
    responseTilt: [[20, 0], [50, 0.2], [120, -0.2], [400, 0.1], [1200, -0.2], [3000, 0.2], [8000, -0.2], [16000, -0.4], [20000, -0.8]],
    resonances: [[290, -48, 20], [700, -52, 24]],
    coverageAngleDeg: 90,
    dimensionsMm: [400, 200, 400],
    massKg: 19,
    thdPercent: 0.2,
    roomCoupling: 'low',
    confidence: 'ESTIMATED',
    accent: '#3f7f8c',
    tags: ['active', 'cardioid', 'dsp', 'sealed', 'six-amplifier'],
    notes:
      'Six drivers and six amplifier channels in a cabinet the size of a bookshelf speaker. The four side and rear facing woofers are fed a frequency-dependent delay and level so that their output cancels the rearward radiation of the front driver, making the whole system cardioid from roughly 80 Hz upward. That is the single most important thing about it in a room: front-wall reflection energy, which normally builds a cancellation notch somewhere between 60 and 150 Hz, is largely removed, so the profile carries low room coupling despite 20 Hz extension from a sealed box the size of a shoebox. DSP crossovers are steep and phase linearised, and the bass extension is achieved electrically against excursion limits rather than by cabinet volume, so its distortion rises faster below 40 Hz than the large ported monitors here.',
    provenanceNote:
      'Six-driver active cardioid configuration, sealed alignment, DSP crossover and bass extension are from Kii Audio published material. Crossover frequencies, cabinet resonances, coverage angle and anechoic response shape are MODELLED. Sensitivity follows the active convention used across this group: modelled 1 m SPL for a nominal 0 dBu balanced input at factory gain, ESTIMATED. The impedance curve is the line input, not a driver load. Cardioid radiation is described in the notes but the directivity model here is a single coverage angle, not a full polar set.',
  }),
];
